import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Loader,
  Center,
  Card,
  SimpleGrid,
  Divider,
  ThemeIcon,
  SegmentedControl,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { suggestionsApi } from '../api/suggestions';
import type { Suggestion } from '../types';
import { pageContainer } from '../styles/theme.css';
import { getSemester, daysAgoLabel } from '../utils/semester';

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 8 ? 'green' : score >= 5 ? 'orange' : 'gray';
  const label = score >= 8 ? 'Stærkt match' : score >= 5 ? 'Muligt match' : 'Svagt match';
  return (
    <Badge color={color} variant="light" size="sm">
      {label} ({score}p)
    </Badge>
  );
}

function SuggestionCard({
  suggestion,
  onConfirm,
  onReject,
  onReset,
  mode,
  loading,
}: {
  suggestion: Suggestion;
  onConfirm?: () => void;
  onReject?: () => void;
  onReset?: () => void;
  mode: 'pending' | 'rejected';
  loading: boolean;
}) {
  const navigate = useNavigate();
  const { item, claim } = suggestion;

  return (
    <Card withBorder shadow="sm" radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <ScoreBadge score={suggestion.score} />
          <Text size="xs" c="dimmed">
            #{suggestion.id}
          </Text>
        </Group>

        <SimpleGrid cols={2} spacing="sm">
          {/* Found item */}
          <Stack gap={6}>
            <Text size="xs" fw={700} tt="uppercase" c="dimmed">
              Fundet genstand
            </Text>
            {item.cover_image && (
              <img
                src={item.cover_image}
                alt={item.title}
                style={{ width: '100%', aspectRatio: '3/2', objectFit: 'cover', borderRadius: 6 }}
              />
            )}
            <Text
              size="sm"
              fw={600}
              style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
              onClick={() => navigate(`/items/${item.id}`)}
            >
              {item.title}
            </Text>
            {(item.color || item.brand) && (
              <Text size="xs" c="dimmed">
                {[item.color, item.brand].filter(Boolean).join(' · ')}
              </Text>
            )}
            <Group gap={4}>
              <Badge size="xs" variant="light" color="blue" tt="none">
                {getSemester(item.found_date)}
              </Badge>
              <Text size="xs" c="dimmed">{daysAgoLabel(item.found_date)}</Text>
            </Group>
          </Stack>

          {/* Lost claim */}
          <Stack gap={6}>
            <Text size="xs" fw={700} tt="uppercase" c="dimmed">
              Efterlysning
            </Text>
            {claim.cover_image && (
              <img
                src={claim.cover_image}
                alt={claim.description}
                style={{ width: '100%', aspectRatio: '3/2', objectFit: 'cover', borderRadius: 6 }}
              />
            )}
            <Text
              size="sm"
              fw={600}
              lineClamp={2}
              style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
              onClick={() => navigate(`/lost-claims/${claim.id}`)}
            >
              {claim.description}
            </Text>
            {(claim.color || claim.brand) && (
              <Text size="xs" c="dimmed">
                {[claim.color, claim.brand].filter(Boolean).join(' · ')}
              </Text>
            )}
            <Text size="xs" c="dimmed">{claim.contact_name}</Text>
          </Stack>
        </SimpleGrid>

        <Divider />

        <Group gap="xs">
          {mode === 'pending' ? (
            <>
              <Button
                size="xs"
                color="green"
                variant="light"
                onClick={onConfirm}
                loading={loading}
                style={{ flex: 1 }}
              >
                Bekræft match
              </Button>
              <Button
                size="xs"
                color="red"
                variant="subtle"
                onClick={onReject}
                loading={loading}
                style={{ flex: 1 }}
              >
                Ikke et match
              </Button>
            </>
          ) : (
            <Button
              size="xs"
              variant="light"
              onClick={onReset}
              loading={loading}
              style={{ flex: 1 }}
            >
              Genopret forslag
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

export function Matches() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'pending' | 'rejected'>('pending');

  const { data: suggestions, isLoading, error } = useQuery({
    queryKey: ['suggestions', tab],
    queryFn: () => suggestionsApi.list(tab),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status, claimId: _claimId }: { id: number; status: 'confirmed' | 'rejected' | 'pending'; claimId: number }) =>
      suggestionsApi.update(id, status),
    onSuccess: (_, { status, claimId }) => {
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      qc.invalidateQueries({ queryKey: ['lost-claims'] });
      if (status === 'confirmed') {
        navigate(`/lost-claims/${claimId}`);
      } else if (status === 'rejected') {
        notifications.show({ message: 'Forslag afvist', color: 'gray' });
      } else {
        notifications.show({ message: 'Forslag genoprettet', color: 'blue' });
      }
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  const resetAllMutation = useMutation({
    mutationFn: suggestionsApi.resetAllRejected,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      notifications.show({ message: 'Alle afviste forslag genoprettet', color: 'blue' });
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  const pendingCount = tab === 'pending' ? (suggestions?.length ?? 0) : 0;

  return (
    <div className={pageContainer}>
      <Stack gap="md">
        <Group justify="space-between">
          <Stack gap={2}>
            <Title order={2}>Mulige match</Title>
            <Text size="sm" c="dimmed">
              Genstande og efterlysninger der kan høre sammen
            </Text>
          </Stack>
          <Group gap="xs">
            {pendingCount > 0 && (
              <Badge size="lg" color="red" variant="filled" circle>
                {pendingCount}
              </Badge>
            )}
            <Button component={Link} to="/matches/settings" variant="default" size="xs">
              Indstillinger
            </Button>
          </Group>
        </Group>

        <Group justify="space-between">
          <SegmentedControl
            size="xs"
            value={tab}
            onChange={(v) => setTab(v as 'pending' | 'rejected')}
            data={[
              { label: 'Ventende', value: 'pending' },
              { label: 'Afviste', value: 'rejected' },
            ]}
          />
          {tab === 'rejected' && suggestions && suggestions.length > 0 && (
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => resetAllMutation.mutate()}
              loading={resetAllMutation.isPending}
            >
              Nulstil alle
            </Button>
          )}
        </Group>

        {isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}

        {error && <Text c="red">Fejl: {(error as Error).message}</Text>}

        {!isLoading && !error && suggestions?.length === 0 && (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <ThemeIcon size="xl" variant="light" color="gray" radius="xl">
                ✓
              </ThemeIcon>
              <Text c="dimmed">
                {tab === 'pending' ? 'Ingen ventende forslag' : 'Ingen afviste forslag'}
              </Text>
            </Stack>
          </Center>
        )}

        {suggestions && suggestions.length > 0 && (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {suggestions.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                mode={tab}
                onConfirm={() => updateMutation.mutate({ id: s.id, status: 'confirmed', claimId: s.claim.id })}
                onReject={() => updateMutation.mutate({ id: s.id, status: 'rejected', claimId: s.claim.id })}
                onReset={() => updateMutation.mutate({ id: s.id, status: 'pending', claimId: s.claim.id })}
                loading={updateMutation.isPending}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </div>
  );
}
